import React, { useEffect, useRef, useState } from 'react';

/**
 * Горизонтальна смуга прокрутки, що тримається внизу ЕКРАНА, поки на ньому
 * видно хоч шматок таблиці.
 *
 * НАВІЩО (менеджер 26.10). Рідна смуга живе під останнім рядком: щоб посунути
 * таблицю вбік, треба було догортати донизу, посунути й гортати назад до
 * потрібного рядка. Ця смуга стоїть одразу під контейнером таблиці, а поки
 * низ таблиці нижче краю екрана (а верх — на екрані), вона «відʼїжджає» до
 * низу екрана.
 *
 * 🚨 `position: fixed`, А НЕ `sticky`. Перша версія була sticky — і в
 * кабінеті не прилипала ніколи: `<main>` оболонки має `overflow-x: hidden`,
 * а це робить його рамкою для sticky (overflow-y стає auto), хоча сам він
 * не гортається. Міняти оболонку означало б зачепити sticky на всіх
 * сторінках, тож смуга сама стежить за положенням таблиці.
 *
 * Своя, а не другий `overflow-x: auto`: на телефонах смуги прокрутки
 * накладні й невидимі, тож за «копію рідної» не було б за що вхопитись.
 *
 * `target` — ref контейнера з `overflow-x: auto`; рідну горизонтальну смугу
 * в ньому варто сховати (клас `hscroll-hidden`), інакше внизу таблиці їх дві.
 */
export default function StickyHScroll({ target }) {
  const [m, setM] = useState({ client: 0, scroll: 0, left: 0, float: null });
  const drag = useRef(null);
  const track = useRef(null);

  useEffect(() => {
    const el = target.current;
    if (!el) return undefined;
    const read = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // Плаває, коли верх таблиці вже на екрані, а низ — ще нижче краю.
      const float = (r.top < vh - BAR_H * 2 && r.bottom > vh)
        ? { left: r.left, width: r.width } : null;
      setM({ client: el.clientWidth, scroll: el.scrollWidth, left: el.scrollLeft, float });
    };
    read();
    el.addEventListener('scroll', read, { passive: true });
    window.addEventListener('resize', read);
    // capture: сторінку може гортати не window, а будь-який предок.
    window.addEventListener('scroll', read, { passive: true, capture: true });
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(read);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
      // Таблицю зсуває те, що НАД нею (блок пароля, підсумки довантажуються
      // пізніше), — її власний розмір при цьому не міняється. Без цього смуга
      // «плавала» внизу екрана, коли таблиця була ще за його краєм.
      ro.observe(document.body);
    }
    const tick = setInterval(read, 1000);
    return () => {
      el.removeEventListener('scroll', read);
      window.removeEventListener('resize', read);
      window.removeEventListener('scroll', read, { capture: true });
      if (ro) ro.disconnect();
      clearInterval(tick);
    };
  }, [target]);

  const max = m.scroll - m.client;
  if (max <= 1) return null;
  const trackW = track.current?.clientWidth || m.client;
  const thumbW = Math.max(40, trackW * m.client / m.scroll);
  const thumbX = (trackW - thumbW) * (m.left / max);

  const scrollTo = (x) => {
    const el = target.current;
    if (el) el.scrollLeft = Math.max(0, Math.min(max, x));
  };
  const onDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    drag.current = { x: e.clientX, left: m.left };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const k = max / Math.max(1, trackW - thumbW);
    scrollTo(drag.current.left + (e.clientX - drag.current.x) * k);
  };
  const onUp = () => { drag.current = null; };
  // Клік по доріжці — сторінка вбік, як у рідної смуги.
  const onTrack = (e) => {
    const r = track.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    scrollTo(m.left + (x < thumbX ? -m.client : m.client) * 0.9);
  };

  const dock = m.float
    ? { ...S.dock, ...S.fixed, left: m.float.left, width: m.float.width }
    : S.dock;
  return (
    <div style={{ height: BAR_H }}>
    <div style={dock}>
      <div ref={track} style={S.track} onPointerDown={onTrack}>
        <div style={{ ...S.thumb, width: thumbW, transform: `translateX(${thumbX}px)` }}
             onPointerDown={onDown} onPointerMove={onMove}
             onPointerUp={onUp} onPointerCancel={onUp} />
      </div>
    </div>
    </div>
  );
}

const BAR_H = 24;

const S = {
  dock:  { boxSizing: 'border-box', height: BAR_H, padding: '6px 0',
           background: 'rgba(255,255,255,0.92)' },
  fixed: { position: 'fixed', bottom: 0, zIndex: 50, padding: '6px 8px',
           borderTop: '1px solid #E5E7EB', boxShadow: '0 -2px 8px rgba(15,23,42,0.06)' },
  track: { position: 'relative', height: 12, borderRadius: 6, background: '#EEF0F3',
           cursor: 'pointer', touchAction: 'none' },
  thumb: { position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: 6,
           background: '#9AA3AF', cursor: 'grab', touchAction: 'none' },
};
